import { Stack, Toolbar, useTheme, Theme, CSSObject, Drawer, useMediaQuery } from "@mui/material";
import { matchRoutes, useLocation, useNavigate } from "react-router-dom";
import { DashboardMenuButton } from "./DashboardLinkListItem";
import { dashboardWidgets } from "../DashboardLayout/SortableItem";
import { ReactNode } from "react";
import { Dashboard } from "@mui/icons-material";
import { Permission, useIsAllowed } from "@sanmanuel/permissions";
import { useMobile } from "hooks/Mobile/useMobile";

interface IDashboardMenuItem {
  title: ReactNode;
  url: string;
  icon: ReactNode;
  permissionsRequired?: Permission | Permission[];
}

const menuItems: IDashboardMenuItem[] = [
  { title: "Dashboard", url: "/Dashboard", icon: <Dashboard /> },
  ...Object.values(dashboardWidgets)
    .filter((w) => w.detailsRoute)
    .map((w) => ({
      title: w.title,
      icon: w.icon,
      url: w.detailsRoute!,
      permissionsRequired: w.permissionsRequired,
    })),
];

type Prop = {
  open: boolean;
  handleDrawerOpen: () => void;
  handleDrawerClose: () => void;
};

export const DashboardAppDrawer = ({ open, handleDrawerClose, handleDrawerOpen }: Prop) => {
  const theme = useTheme();
  const navigate = useNavigate();
  const location = useLocation();
  const isAllowed = useIsAllowed();
  const isDesktop = useMediaQuery(theme.breakpoints.up("lg"));
  const isMobile = useMobile();

  const openedMixin = (theme: Theme): CSSObject => ({
    transition: theme.transitions.create("width", {
      easing: theme.transitions.easing.sharp,
      duration: theme.transitions.duration.enteringScreen,
    }),
  });

  const closedMixin = (theme: Theme): CSSObject => ({
    transition: theme.transitions.create("width", {
      easing: theme.transitions.easing.sharp,
      duration: theme.transitions.duration.leavingScreen,
    }),
  });

  const mixin = open ? openedMixin(theme) : closedMixin(theme);
  return (
    <Drawer
      anchor={"left"}
      open={open}
      onClose={handleDrawerClose}
      variant={isDesktop ? "permanent" : "temporary"}
      sx={
        isDesktop
          ? {
              ...mixin,
              [`& .MuiDrawer-paper`]: open ? openedMixin(theme) : closedMixin(theme),
            }
          : isMobile
          ? {
              [`& .MuiDrawer-paper`]: {
                width: "100vw",
              },
            }
          : {}
      }
    >
      <Toolbar />
      <Stack
        spacing={1}
        sx={{ px: 1, my: 2 }}
        onMouseOver={(event) => {
          if (!open) {
            handleDrawerOpen();
          }
        }}
        onMouseLeave={(e) => {
          handleDrawerClose();
        }}
      >
        {menuItems
          .filter((mi) => (mi.permissionsRequired ? isAllowed(mi.permissionsRequired) : true))
          .map((item, index) => (
            <DashboardMenuButton
              key={`dashboard-item-${index}`}
              value={open ? item.title : undefined}
              icon={item.icon}
              onClick={() => {
                navigate(item.url);
                handleDrawerClose();
              }}
              isSelected={(matchRoutes([{ path: item.url }], location) ?? []).length > 0}
            />
          ))}
      </Stack>
    </Drawer>
  );
};
